<template lang="pug">
.sticker-card
  .sticker-card__header(@click="expanded = !expanded")
    .sticker-card__thumb
      img(v-if="thumbUrl" :src="thumbUrl" :alt="item.sticker?.title || ''")
      fai(v-else type="fal" icon="image").size-18
    .sticker-card__info
      p.sticker-card__name {{ item.sticker?.title || 'Sticker' }}
      p.sticker-card__meta
        | {{ codeCountLabel }}
        span.sticker-card__tagged(v-if="isOtherCreator")
          fai(type="fal" icon="tag").size-14
          | tagged
    .sticker-card__actions(@click.stop)
      span.sticker-card__badge.sticker-card__badge--tagged(v-if="notLive") Not live
      span.sticker-card__badge(
        :class="isOtherCreator ? 'sticker-card__badge--tagged' : 'sticker-card__badge--new'"
      ) {{ isOtherCreator ? 'Tagged' : 'Own' }}
      button.sticker-card__icon-btn.sticker-card__icon-btn--neutral(
        type="button"
        title="Move up"
        :disabled="!canMoveUp"
        @click="move(-1)"
      )
        fai(type="fal" icon="arrow-up").size-14
      button.sticker-card__icon-btn.sticker-card__icon-btn--neutral(
        type="button"
        title="Move down"
        :disabled="!canMoveDown"
        @click="move(1)"
      )
        fai(type="fal" icon="arrow-down").size-14
      a.sticker-card__icon-btn.sticker-card__icon-btn--neutral(
        v-if="item.sticker?.id"
        :href="stickerEditUrl"
        target="_blank"
        rel="noopener"
        title="Edit sticker"
      )
        fai(type="fal" icon="external-link-alt").size-14
      button.sticker-card__icon-btn(type="button" title="Remove" @click="removeItem")
        fai(type="fal" icon="trash").size-14
      span.sticker-card__chevron(
        :class="{ 'is-open': expanded }"
        @click="expanded = !expanded"
      )
        fai(type="fal" icon="chevron-down").size-14

  .sticker-card__body(v-if="expanded")
    .codes__note(v-if="notLive")
      span ⚠️
      span
        | This sticker is {{ (item.sticker?.status || '').toLowerCase() }}: collectors can't open or
        | collect it until it is active. Submit it for approval from the sticker editor.
    .clue
      .clue__head
        span.caps-label Clue / Hint (Optional)
        button.clue__link(type="button" @click="toggleClue")
          | {{ showClue ? 'Remove' : '+ Add clue' }}
      textarea.field-input.field-input--textarea(
        v-if="showClue"
        v-model="localClue"
        rows="2"
        maxlength="200"
        placeholder='Hint shown to collectors, e.g. "Find us at the Saturday farmers market booth"'
        @blur="flush"
      )

    .codes
      .codes__head
        p.caps-label.codes__title
          fai(type="fal" icon="key").size-14
          | Unlock codes
        el-tooltip(
          :disabled="!requiresCodeDisabled"
          :content="requiresCodeBlockedReason"
          placement="top"
        )
          label.codes__required
            span Required
            button.codes__toggle(
              type="button"
              :class="{ 'is-on': item.requiresCode }"
              :disabled="requiresCodeDisabled || savingItem"
              @click="setRequiresCode(!item.requiresCode)"
            )
      .codes__note(v-if="!item.requiresCode")
        span ⚡
        span No code required — this sticker is automatically available to all collectors.

      .codes__list(v-if="item.codes?.length")
        .code-row(
          v-for="code in item.codes"
          :key="code.id"
          :class="{ 'is-inactive': !code.isActive }"
        )
          span.code-row__code {{ code.code }}
          span.code-row__uses
            | {{ code.uses ?? 0 }} use{{ (code.uses ?? 0) !== 1 ? 's' : '' }}
            template(v-if="!code.isActive")  · inactive
          .code-row__max
            input.code-row__max-input(
              type="number"
              min="1"
              placeholder="∞"
              :value="code.maxUses ?? ''"
              @change="updateCodeMaxUses(code, $event)"
            )
            span.code-row__max-label max
          button.code-row__reactivate(
            v-if="!code.isActive"
            type="button"
            @click="setCodeActive(code, true)"
          ) Reactivate
          button.code-row__remove(
            v-else
            type="button"
            title="Remove code"
            @click="deleteCode(code)"
          )
            fai(type="fal" icon="times").size-14

      .codes__add(v-if="item.requiresCode")
        input.codes__add-input(
          v-model="newCode"
          :placeholder="codeInputPlaceholder"
          @keydown.enter="addCode"
        )
        button.codes__add-btn(
          type="button"
          :disabled="addingCode || !canAddCode"
          @click="addCode"
        )
          fai(type="fal" icon="plus").size-16
          | Add
</template>

<script lang="ts">
import {
  computed, defineComponent, ref, watch,
} from 'vue';
import { useRouter } from 'vue-router';
import { ElMessage, ElMessageBox } from 'element-plus';
import globalShared from '@/shared';
import {
  mutateStickerCollectionItem,
  mutateStickerCollectionCode,
  removeStickerCollectionItem,
  removeStickerCollectionCode,
} from '@/graphs/gql';
import type {
  StickerCollectionCmsItem,
  StickerCollectionCode,
  StickerCollectionCodeFormat,
} from '@/data/stickerCollection.types';
import withGraphErrors from '@/utils/graphqlResponse';
import isStickerUnavailable from '@/utils/stickerCollectionSticker';
import {
  codeInputPlaceholder as placeholderForFormat,
  codeRequirementBlockedReason,
  parseMaxUses,
} from '../shared';

export default defineComponent({
  name: 'CollectionStickerItemCard',
  props: {
    item: {
      type: Object as () => StickerCollectionCmsItem,
      required: true,
    },
    collectionId: { type: String, required: true },
    collectionOwnerId: { type: String, default: '' },
    codeFormat: { type: String as () => StickerCollectionCodeFormat, default: 'ALPHANUMERIC' },
    canMoveUp: { type: Boolean, default: true },
    canMoveDown: { type: Boolean, default: true },
  },
  emits: ['refresh', 'move'],
  setup(props, { emit }) {
    const router = useRouter();
    const { makeGraphRequest, getAuthToken, removeItemGetConfirm } = globalShared();
    const expanded = ref(true);
    const localClue = ref(props.item.clue || '');
    const showClue = ref(Boolean(props.item.clue));
    const savingItem = ref(false);
    const addingCode = ref(false);
    const newCode = ref('');

    watch(
      () => props.item.clue,
      (clue) => {
        localClue.value = clue || '';
        if (clue) showClue.value = true;
      },
    );

    // Full image first: a sticker's thumbnailUrl can be stale (shared by several stickers).
    const thumbUrl = computed(() => props.item.sticker?.url || props.item.sticker?.thumbnailUrl);

    const stickerEditUrl = computed(
      () => router.resolve(`/manage-content/media/stickers-v2/${props.item.sticker?.id}`).href,
    );

    const isOtherCreator = computed(() => {
      const stickerOwnerId = props.item.sticker?.entityId;
      return Boolean(
        props.collectionOwnerId && stickerOwnerId && stickerOwnerId !== props.collectionOwnerId,
      );
    });

    const notLive = computed(() => isStickerUnavailable(props.item.sticker));

    const requiresCodeBlockedReason = computed(
      () => codeRequirementBlockedReason(props.item.sticker, props.collectionOwnerId),
    );
    // Already-required items stay editable so the creator can switch the requirement off.
    const requiresCodeDisabled = computed(
      () => !props.item.requiresCode && Boolean(requiresCodeBlockedReason.value),
    );

    const codeCountLabel = computed(() => {
      const n = props.item.codes?.length ?? 0;
      return `${n} unlock code${n !== 1 ? 's' : ''}`;
    });

    async function saveItem(input: Record<string, unknown>): Promise<boolean> {
      savingItem.value = true;
      try {
        const response = await withGraphErrors(makeGraphRequest(
          'mutation',
          mutateStickerCollectionItem,
          { input: { id: props.item.id, collectionId: props.collectionId, ...input } },
          getAuthToken(),
        ));
        if (response?.errors?.length) {
          ElMessage.error(response.errors[0]?.message || 'Failed to save sticker');
          return false;
        }
        emit('refresh');
        return true;
      } catch (e) {
        console.error(e);
        ElMessage.error('Failed to save sticker');
        return false;
      } finally {
        savingItem.value = false;
      }
    }

    /** Saves a changed clue. Called on blur and by "Save Collection". */
    async function flush(): Promise<boolean> {
      const clue = showClue.value ? localClue.value.trim() : '';
      if (clue === (props.item.clue || '')) return true;
      // `clue: ""` clears it on the backend.
      return saveItem({ clue });
    }

    function toggleClue() {
      if (showClue.value) {
        showClue.value = false;
        localClue.value = '';
        flush();
      } else {
        showClue.value = true;
      }
    }

    function setRequiresCode(value: boolean) {
      saveItem({ requiresCode: value });
    }

    function move(delta: number) {
      emit('move', { itemId: props.item.id, delta });
    }

    function removeItem() {
      removeItemGetConfirm(async () => {
        const response = await withGraphErrors(makeGraphRequest(
          'mutation',
          removeStickerCollectionItem,
          { itemId: props.item.id },
          getAuthToken(),
        ));
        if (response?.errors?.length) {
          ElMessage.error(response.errors[0]?.message || 'Failed to remove sticker');
          return false;
        }
        emit('refresh');
        return true;
      }, {
        title: 'Remove sticker',
        message: 'This sticker will be removed from the collection. The sticker itself is kept.',
        confirmText: 'Remove',
      });
    }

    async function mutateCode(input: Record<string, unknown>, fallback: string) {
      const response = await withGraphErrors(makeGraphRequest(
        'mutation',
        mutateStickerCollectionCode,
        { input },
        getAuthToken(),
      ));
      if (response?.errors?.length) {
        ElMessage.error(response.errors[0]?.message || fallback);
        return false;
      }
      emit('refresh');
      return true;
    }

    const codeInputPlaceholder = computed(() => placeholderForFormat(props.codeFormat));
    // Custom-format collections have no generated codes, so a code must be typed.
    const canAddCode = computed(
      () => props.codeFormat !== 'CUSTOM' || Boolean(newCode.value.trim()),
    );

    async function addCode() {
      if (addingCode.value || !canAddCode.value) return;
      addingCode.value = true;
      try {
        // Omitting `code` lets the backend generate one; omitting `maxUses` applies the default.
        const input: Record<string, unknown> = { itemId: props.item.id };
        if (newCode.value.trim()) input.code = newCode.value.trim().toUpperCase();
        if (await mutateCode(input, 'Failed to add code')) newCode.value = '';
      } finally {
        addingCode.value = false;
      }
    }

    // maxUses is always applied on edit, so it is sent with every code update.
    function updateCodeMaxUses(code: StickerCollectionCode, event: Event) {
      const maxUses = parseMaxUses((event.target as HTMLInputElement).value);
      if (maxUses === (code.maxUses ?? null)) return;
      mutateCode({ id: code.id, maxUses, isActive: code.isActive }, 'Failed to update code');
    }

    function setCodeActive(code: StickerCollectionCode, isActive: boolean) {
      mutateCode({ id: code.id, maxUses: code.maxUses ?? null, isActive }, 'Failed to update code');
    }

    async function deleteCode(code: StickerCollectionCode) {
      if (code.uses) {
        try {
          await ElMessageBox.confirm(
            'This code has been used, so it will be deactivated instead of deleted.',
            'Remove code',
            { type: 'warning', customClass: 'c-confirm', confirmButtonText: 'Continue' },
          );
        } catch {
          return;
        }
      }
      const response = await withGraphErrors(makeGraphRequest(
        'mutation',
        removeStickerCollectionCode,
        { codeId: code.id },
        getAuthToken(),
      ));
      if (response?.errors?.length) {
        ElMessage.error(response.errors[0]?.message || 'Failed to remove code');
        return;
      }
      emit('refresh');
    }

    return {
      expanded,
      localClue,
      showClue,
      savingItem,
      addingCode,
      newCode,
      thumbUrl,
      stickerEditUrl,
      isOtherCreator,
      notLive,
      requiresCodeDisabled,
      requiresCodeBlockedReason,
      codeCountLabel,
      flush,
      toggleClue,
      setRequiresCode,
      move,
      removeItem,
      codeInputPlaceholder,
      canAddCode,
      addCode,
      updateCodeMaxUses,
      setCodeActive,
      deleteCode,
    };
  },
});
</script>

<style lang="scss" scoped>
@import '../sticker-card';
</style>
